import { CountryCode } from 'libphonenumber-js';
import { PhoneNumberCountryStatus, PhoneNumberHelper } from 'src/lib/fliko/helpers/phone-number.helper';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { ResidentType } from 'src/lib/fliko/models/person/resident.data';
import { ResidentUnitsVM } from 'src/app/view/users/services/models/users-view-models';

export interface ReceiverPhoneIntention {
  residentIds: string[] | null;
  residentType: ResidentType | null;
}

export interface ReceiverPhoneEligibility {
  selectableResidents: ResidentUnitsVM[];
  foreignResidents: ResidentUnitsVM[];
  invalidResidents: ResidentUnitsVM[];
  phoneStatuses: ReadonlyMap<string, PhoneNumberCountryStatus>;
}

/**
 * Classifies selectable and excluded residents for a country-limited SMS.
 * Excluded recipients are deduplicated by resident id and never truncated.
 * @param allResidents
 * @param allowedCountries
 * @param intention
 */
export function calculateReceiverPhoneEligibility(
  allResidents: ResidentUnitsVM[],
  allowedCountries: readonly CountryCode[],
  intention: ReceiverPhoneIntention
): ReceiverPhoneEligibility {
  if (allowedCountries.length === 0) {
    return {
      selectableResidents: allResidents,
      foreignResidents: [],
      invalidResidents: [],
      phoneStatuses: new Map()
    };
  }

  const intendedIds = intention.residentIds == null ? null : new Set(intention.residentIds);
  const statuses = new Map<string, PhoneNumberCountryStatus>();
  const excludedIds = new Set<string>();
  const selectableResidents: ResidentUnitsVM[] = [];
  const foreignResidents: ResidentUnitsVM[] = [];
  const invalidResidents: ResidentUnitsVM[] = [];

  allResidents.forEach((residentVM) => {
    const residentId = residentVM.resident.id;
    let status = statuses.get(residentId);
    if (!status) {
      status = PhoneNumberHelper.statusForCountries(ToString.getPhone(residentVM.resident), allowedCountries);
      statuses.set(residentId, status);
    }

    if (status === 'allowed') {
      selectableResidents.push(residentVM);
      return;
    }
    if (excludedIds.has(residentId) || !isIntendedReceiver(residentVM, intendedIds, intention.residentType)) {
      return;
    }

    excludedIds.add(residentId);
    if (status === 'foreign') foreignResidents.push(residentVM);
    else invalidResidents.push(residentVM);
  });

  return {
    selectableResidents,
    foreignResidents,
    invalidResidents,
    phoneStatuses: statuses
  };
}

/**
 * @param residentVM
 * @param intendedIds
 * @param residentType
 */
function isIntendedReceiver(
  residentVM: ResidentUnitsVM,
  intendedIds: Set<string> | null,
  residentType: ResidentType | null
): boolean {
  const matchesType = !!residentType && residentVM.type === residentType;
  if (intendedIds === null) return residentType ? matchesType : true;
  return intendedIds.has(residentVM.resident.id) || matchesType;
}
